import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { Role } from '../../types';

interface RoleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (roleData: Partial<Role>) => void;
  role: Role | null;
}

const availablePermissions = [
  'manage_users',
  'manage_roles',
  'manage_cases',
  'view_cases',
  'manage_documents',
  'view_documents',
  'manage_billing',
  'view_reports',
  'view_own_cases',
  'view_own_documents',
];

export const RoleDialog: React.FC<RoleDialogProps> = ({
  open,
  onClose,
  onSave,
  role,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    permissions: [] as string[],
  });

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name,
        permissions: role.permissions,
      });
    } else {
      setFormData({
        name: '',
        permissions: [],
      });
    }
  }, [role]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {role ? 'Edit Role' : 'Add Role'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Role Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            />

            <FormControl fullWidth>
              <InputLabel>Permissions</InputLabel>
              <Select
                multiple
                value={formData.permissions}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  permissions: typeof e.target.value === 'string' 
                    ? e.target.value.split(',')
                    : e.target.value
                }))}
                input={<OutlinedInput label="Permissions" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value.replace('_', ' ')}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {availablePermissions.map((permission) => (
                  <MenuItem key={permission} value={permission}>
                    <Checkbox checked={formData.permissions.indexOf(permission) > -1} />
                    <ListItemText primary={permission.replace('_', ' ')} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
          >
            {role ? 'Save Changes' : 'Add Role'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};